import React, { useEffect } from 'react';
import { scroller } from 'react-scroll';
import { SuggestionDataItem } from 'react-mentions';

import Button, { BUTTON_TYPES } from 'buyside/components/presentation/Button/button';
import IdeaUpdateCommentForm from 'buyside/components/presentation/IdeaUpdates/IdeaUpdateCommentForm/ideaUpdateCommentForm';
import IdeaUpdateComment from 'buyside/components/presentation/IdeaUpdates/IdeaUpdateComments/IdeaUpdateComment';
import ShowPageCommentLoading from 'buyside/components/presentation/ShowPageComments/ShowPageComment/showPageCommentLoading';

import {
  Maybe,
  useIdeaUpdateCommentsQuery,
  IdeaUpdateCommentsQuery
} from 'interfaces/graphql-typings';

import styles from 'buyside/components/presentation/IdeaUpdates/IdeaUpdateComments/ideaUpdateCommentsStyles.scss';

const IdeaUpdateComments: React.FC<{
  authorId: string;
  authorName: Maybe<string>;
  ideaUpdateId: string;
  isAdmin: boolean;
  isAuthor: boolean;
  canWriteComments: boolean;
  canViewUserDetails: boolean;
  currentUserId: string;
  highlightedCommentId?: string;
  postCommentDivId: string;
  showPostComment: boolean;
  isIdeaAnonymous: boolean;
  isNonBuyside: boolean;
}> = ({
  authorId,
  authorName,
  ideaUpdateId,
  isAdmin,
  isAuthor,
  canWriteComments,
  canViewUserDetails,
  currentUserId,
  highlightedCommentId,
  postCommentDivId,
  showPostComment,
  isIdeaAnonymous,
  isNonBuyside
}): React.ReactElement => {
  const { data, loading, error, fetchMore } = useIdeaUpdateCommentsQuery({
    variables: { id: ideaUpdateId, controlName: !isAuthor }
  });

  useEffect(() => {
    if (showPostComment) {
      scroller.scrollTo(postCommentDivId, {
        duration: 500,
        delay: 0,
        smooth: 'easeInOutQuint',
        offset: -(65 + 45 + 15) // -(top navigation height + page navigator height + some padding)
      });
    }
  }, [showPostComment, postCommentDivId, loading]);

  if (loading) return <ShowPageCommentLoading />;
  if (error) throw error;

  const { comments, commentMentionables } = data?.ideaUpdate || {};
  const { canCommentAnonymously, profileImageUrl, isAllocator } = data?.currentUser || {};

  const hasNextPage = comments?.pageInfo?.hasNextPage;
  const endCursor = comments?.pageInfo?.endCursor;

  const [parentComments, allChildComments] = (comments?.edges || []).reduce<
    NonNullable<NonNullable<IdeaUpdateCommentsQuery['ideaUpdate']['comments']>['edges']>[number][][]
  >(
    (acc, edge) => {
      if (!edge || !edge?.node) return acc;

      acc[edge.node.parentId ? 1 : 0].push(edge);
      return acc;
    },
    [[], []]
  );

  return (
    <div className={styles.ideaUpdateComments}>
      {canWriteComments && (
        <div id={postCommentDivId}>
          <IdeaUpdateCommentForm
            canCommentAnonymously={Boolean(canCommentAnonymously)}
            ideaUpdateId={ideaUpdateId}
            isAllocator={Boolean(isAllocator)}
            isAuthor={Boolean(isAuthor)}
            isIdeaAnonymous={isIdeaAnonymous}
            mentionables={commentMentionables as SuggestionDataItem[]}
            profileImageUrl={profileImageUrl}
          />
        </div>
      )}
      {parentComments?.map(edge => {
        const comment = edge?.node;

        return comment ? (
          <IdeaUpdateComment
            allChildComments={allChildComments.map(c => c?.node || null) || []}
            authorId={authorId}
            authorName={authorName}
            canCommentAnonymously={Boolean(canCommentAnonymously)}
            canWriteComments={Boolean(canWriteComments)}
            canViewUserDetails={
              canViewUserDetails || comment.user?.id === currentUserId || isAuthor
            }
            comment={comment}
            currentUserId={currentUserId}
            ideaUpdateId={ideaUpdateId}
            highlightedCommentId={highlightedCommentId}
            isAdmin={isAdmin}
            isAuthor={isAuthor}
            mentionables={commentMentionables as SuggestionDataItem[]}
            isAllocator={Boolean(isAllocator)}
            isIdeaAnonymous={isIdeaAnonymous}
            isNonBuyside={isNonBuyside}
            key={comment.id}
          />
        ) : null;
      })}
      {hasNextPage && endCursor && (
        <Button
          onClick={(): void => {
            fetchMore({ variables: { cursor: endCursor, id: ideaUpdateId } });
          }}
          text="Load More"
          type={BUTTON_TYPES.LINK}
        />
      )}
      {!comments?.edges?.length && <div className={styles.noComments}>No comments</div>}
    </div>
  );
};

export default IdeaUpdateComments;
